import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Snackbar,
  MenuItem,
  Grid,
} from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, api } from "../context/AuthContext";
import { IconButton } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const INDIGO = "#4F46E5";

function PostJob() {
  const { user, setUser } = useAuth();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    companyName: "",
    location: "",
    workMode: "",
    salary: "",
    stipend: "",
    minExperience: "",
    maxExperience: "",
    requirements: "",
  });
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [quillKey, setQuillKey] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const navigate = useNavigate();
  const [showLimitPopup, setShowLimitPopup] = useState(false);
  const [limitMessage, setLimitMessage] = useState("");
  const [screeningQuestions, setScreeningQuestions] = useState([""]);
  const [limitPlanType, setLimitPlanType] = useState("");

  const handleBuyExtra = () => {
    setShowLimitPopup(false);
    if (limitPlanType === 'professional' || limitPlanType === 'professional_overage') {
      navigate('/pricing?type=overage&plan=professional');
    } else {
      navigate('/pricing?type=overage&plan=starter');
    }
  };

  const [usage, setUsage] = useState({ jobs: 0, applications: 0 });

  const fetchUsage = async () => {
    if (!user) return;
    try {
      if (user.plan === 'free_trial' && (user.job_limit || 0) <= 0) {
        setError(
          <Box>
            <Typography variant="body1">
              Your free trial is completed.{" "}
              <Link to="/pricing" style={{ color: "inherit", fontWeight: "bold" }}>
                Upgrade your plan
              </Link>
            </Typography>
          </Box>
        );
      }
    } catch (err) {
      console.error("Failed to fetch usage info:", err);
    }
  };

  useEffect(() => {
    const hasSubscription =
        user?.has_active_subscription === true ||
        user?.has_active_subscription === 1 ||
        user?.has_active_subscription === "true";

    if (user && !hasSubscription && user.role !== "super_admin") {
      setError(
        <Box>
          <Typography variant="body1">
            You need an active subscription to post jobs.{" "}
            <Link to="/pricing" style={{ color: "inherit", fontWeight: "bold" }}>
              Upgrade your plan
            </Link>
          </Typography>
        </Box>
      );
    } else if (user?.plan === 'free_trial') {
      // Fetch usage for free trial display
      fetchUsage();
    }
  }, [user]);

  function handleChange(e) {
    if (e && e.target) {
      setFormData({ ...formData, [e.target.name]: e.target.value });
      if (fieldErrors[e.target.name]) {
        setFieldErrors({ ...fieldErrors, [e.target.name]: "" });
      }
    }
  }

  function handleQuillChange(value) {
    setFormData({ ...formData, description: value });
    if (fieldErrors.description) {
      setFieldErrors({ ...fieldErrors, description: "" });
    }
  }

  const handleScreeningQuestionChange = (index, value) => {
    const updated = [...screeningQuestions];
    updated[index] = value;
    setScreeningQuestions(updated);
  };

  const addScreeningQuestion = () => {
    if (screeningQuestions.length < 6) {
      setScreeningQuestions([...screeningQuestions, ""]);
    }
  };

  const removeScreeningQuestion = (index) => {
    const updated = screeningQuestions.filter((_, i) => i !== index);
    setScreeningQuestions(updated.length ? updated : [""]);
  };
  const panelSx = (bg, border) => ({
    p: 2.5,
    borderRadius: 3,
    border: `1px solid ${border}`,
    background: `linear-gradient(180deg, ${bg} 0%, #ffffff 100%)`,
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.04)",
  });

  const iconBoxSx = (bg, color) => ({
    width: 40,
    height: 40,
    borderRadius: 2,
    backgroundColor: bg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color,
  });
  const getWordCount = (html = "") => {
    const text = html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").trim();
    if (!text) return 0;
    return text.split(/\s+/).filter(Boolean).length;
  };

  const validateJobForm = () => {
    let newErrors = {};

    if (!formData.title.trim()) newErrors.title = "Job Title is required";
    if (!formData.companyName.trim()) newErrors.companyName = "Company Name is required";
    if (!formData.location.trim()) newErrors.location = "Location is required";
    if (!formData.salary.trim()) newErrors.salary = "Salary Range is required";

    if (formData.minExperience === "" || formData.maxExperience === "") {
      newErrors.experienceRange = "Experience range is required";
    } else if (Number(formData.minExperience) > Number(formData.maxExperience)) {
      newErrors.experienceRange =
        "Minimum experience cannot be greater than maximum experience";
    }

    if (!formData.description.trim()) newErrors.description = "Job Description is required";
    if (!formData.requirements.trim()) newErrors.requirements = "Requirements are required";

    return newErrors;
  };

  const handlePreview = (e) => {
    e.preventDefault();
    setError("");
    const newErrors = validateJobForm();

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      return;
    }
    setPreviewOpen(true);
  };

  const handleConfirmPost = async () => {
    setPreviewOpen(false);
    await handleSubmit({ preventDefault: () => {} });
  };

async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    // 1. Validation Logic
    let newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Job Title is required";
    if (!formData.companyName.trim()) newErrors.companyName = "Company Name is required";
    if (!formData.location.trim()) newErrors.location = "Location is required";
    if (!formData.salary.trim()) newErrors.salary = "Salary Range is required";
    if (formData.minExperience === "" || formData.maxExperience === "") {
      newErrors.experienceRange = "Experience range is required";
    } else if (Number(formData.minExperience) > Number(formData.maxExperience)) {
      newErrors.experienceRange =
        "Minimum experience cannot be greater than maximum experience";
    }
    if (!formData.description.trim()) newErrors.description = "Job Description is required";
    if (!formData.requirements.trim()) newErrors.requirements = "Requirements are required";

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      return;
    }

    const hasSubscription =
      user?.has_active_subscription === true ||
      user?.has_active_subscription === 1 ||
      user?.has_active_subscription === "true";

    const isFreeTrialExpired =
      user?.plan === 'free_trial' &&
      user?.free_trial_expires_at &&
      new Date(user.free_trial_expires_at) < new Date();

    const isOverLimit =
      user?.plan === 'free_trial' &&
      (
        (user.job_limit || 0) <= 0 ||
        isFreeTrialExpired
      );

    if ((!hasSubscription || isOverLimit) && user?.role !== "super_admin") {
      setError(
        <Box>
          <Typography variant="body1">
            {isFreeTrialExpired
              ? "Your free trial has expired. "
              : "Your free trial is completed. "}
            <Link to="/pricing" style={{ color: "inherit", fontWeight: "bold" }}>
              Upgrade your plan
            </Link>
          </Typography>
        </Box>
      );
      return;
    }

    setLoading(true);
    setSuccess(false);
    setSuccessMessage("");

    try {
      const jobData = {
        ...formData,
        work_mode: formData.workMode,
        experienceRange: `${formData.minExperience}-${formData.maxExperience}`,
        requirements: formData.requirements.split("\n").filter((r) => r.trim()),
        screening_questions: screeningQuestions.filter((q) => q.trim() !== ""),
      };

      console.log("========== POST JOB DEBUG ==========");

      console.log("USER PLAN:", user?.plan);

      console.log(
        "PROF OVERAGE REMAINING:",
        user?.professional_overage_jobs
      );

      console.log(
        "STARTER OVERAGE REMAINING:",
        user?.overage_jobs
      );

      console.log(
        "HAS SUBSCRIPTION:",
        user?.has_active_subscription
      );

      console.log("POSTING JOB NOW...");

      const response = await api.post(
        "/admin/jobs",
        jobData
      );

      console.log(
        "JOB CREATED SUCCESSFULLY:",
        response.data
      );

      console.log("===================================");
      // --- ADD THESE 6 LINES IMMEDIATELY AFTER ---
            try {
              const userRes = await api.get('/auth/user');
              setUser(userRes.data); // Updates the 1-for-1 overage count in UI
            } catch (uErr) {
              console.error("Failed to refresh user state:", uErr);
            }
            // --------------------------------------------

      setFormData({
        title: "",
        description: "",
        companyName: "",
        location: "",
        workMode: "",
        salary: "",
        stipend: "",
        minExperience: "",
        maxExperience: "",
        requirements: "",
      });
      setScreeningQuestions([""]);
      setFieldErrors({});
      setQuillKey((k) => k + 1);

      setSuccess(true);
      setSuccessMessage("Your Job Has Been Posted Successfully.");
      setError("");

      if (user?.plan === 'free_trial') {
        fetchUsage();
        try {
          const userRes = await api.get('/auth/user');
          setUser(userRes.data);
        } catch (uErr) {
          console.error("Failed to refresh user state:", uErr);
        }
      }
    } catch (err) {
      console.log("========== JOB ERROR DEBUG ==========");

      console.log(
        "STATUS:",
        err.response?.status
      );

      console.log(
        "FULL RESPONSE:",
        err.response?.data
      );

      console.log(
        "MESSAGE:",
        err.response?.data?.message
      );

      console.log(
        "PLAN TYPE:",
        err.response?.data?.planType
      );

      console.log(
        "SHOW BUY EXTRA:",
        err.response?.data?.showBuyExtra
      );

      console.log("=====================================");
      console.error("[DEBUG] Final API failure:", err);

      if (err.response?.status === 403) {
        const planType = err.response?.data?.planType;
        const backendMessage = err.response?.data?.message || "Limit reached.";

        if (planType === 'payg') {
          setError(backendMessage);
          setShowLimitPopup(false);
          return;
        }

        setLimitMessage(backendMessage);
        setLimitPlanType(planType || '');
        setShowLimitPopup(true);
        return;
      }

      const errorMessage = err.response?.data?.message || "Failed to create job.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }


  return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4 }}>


          {error && (
            <Alert severity="error">
              {error}

              {showLimitPopup && user?.plan !== 'payg' && (
                <Box mt={1}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleBuyExtra}
                  >
                    Buy Extra Jobs
                  </Button>
                </Box>
              )}
            </Alert>
          )}

          {user?.plan === 'free_trial' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Free Trial Usage:
              </Typography>
              <Typography variant="body2">
                Jobs Remaining: {user.job_limit ?? 2}
              </Typography>
              <Typography variant="body2">
                Applications Remaining: {user.application_limit ?? 5}
              </Typography>

              {user?.free_trial_expires_at && (
                <Typography variant="body2" color="error" fontWeight="bold">
                  Free Trial Expires:{" "}
                  {new Date(user.free_trial_expires_at).toLocaleString()}
                </Typography>
              )}
            </Alert>
          )}

          {success && (
              <Alert
                severity="success"
                sx={{ mb: 2 }}
                onClose={() => {
                  setSuccess(false);
                  setSuccessMessage("");
                }}
              >
                {successMessage}
              </Alert>
          )}

          <form onSubmit={handlePreview} noValidate>
            {(() => {
              const hasSubscription =
                user?.has_active_subscription === true ||
                user?.has_active_subscription === 1 ||
                user?.has_active_subscription === "true";

              const isFreeTrialExpired =
                user?.plan === "free_trial" &&
                user?.free_trial_expires_at &&
                new Date(user.free_trial_expires_at) < new Date();

              const isFreeTrialOverLimit =
                user?.plan === "free_trial" &&
                ((user?.job_limit || 0) <= 0 || isFreeTrialExpired);

              const canPost =
                user?.role === "super_admin" || (hasSubscription && !isFreeTrialOverLimit);

              return (
                <>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: 3,
                            backgroundColor: "#dff7f2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: INDIGO,
                          }}
                        >
                          <WorkOutlineOutlinedIcon sx={{ fontSize: 30 }} />
                        </Box>

                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 900, color: INDIGO }}>
                            Post A New Job
                          </Typography>
                          <Typography variant="body1" sx={{ color: "#6b7280", mt: 0.5 }}>
                            Fill in the details below to create a new job post.
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={panelSx("#f2fbf8", "#d9f0ea")}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                          <Box sx={iconBoxSx("#dff7f2", INDIGO)}>
                            <WorkOutlineOutlinedIcon fontSize="small" />
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 800, color: INDIGO }}>
                              Job Title *
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Enter job title
                            </Typography>
                          </Box>
                        </Box>

                        <TextField
                          fullWidth
                          placeholder="Enter job title"
                          name="title"
                          value={formData.title}
                          onChange={handleChange}
                          disabled={!canPost}
                          error={!!fieldErrors.title}
                          helperText={fieldErrors.title}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={panelSx("#f8fbff", "#dbe7ff")}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                          <Box sx={iconBoxSx("#e7f0ff", INDIGO)}>
                            <BusinessOutlinedIcon fontSize="small" />
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 800, color: INDIGO }}>
                              Company Name *
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Enter company name
                            </Typography>
                          </Box>
                        </Box>

                        <TextField
                          fullWidth
                          placeholder="Enter company name"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleChange}
                          disabled={!canPost}
                          error={!!fieldErrors.companyName}
                          helperText={fieldErrors.companyName}
                        />
                      </Box>
                    </Grid>

                   <Grid item xs={12}>
                      <Box sx={panelSx("#fbf7ff", "#eadcff")}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                          <Box sx={iconBoxSx("#f0e8ff", INDIGO)}>
                            <LocationOnOutlinedIcon fontSize="small" />
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 800, color: INDIGO }}>
                              Location *
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Enter job location
                            </Typography>
                          </Box>
                        </Box>

                        <TextField
                          fullWidth
                          placeholder="Enter job location"
                          name="location"
                          value={formData.location}
                          onChange={handleChange}
                          disabled={!canPost}
                          error={!!fieldErrors.location}
                          helperText={fieldErrors.location}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={panelSx("#f2fbf8", "#d9f0ea")}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.5 }}>
                          <Box sx={iconBoxSx("#dff7f2", INDIGO)}>
                            <WorkOutlineOutlinedIcon fontSize="small" />
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 800, color: INDIGO }}>
                              Work Mode
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Optional
                            </Typography>
                          </Box>
                        </Box>

                        <TextField
                          fullWidth
                          select
                          name="workMode"
                          value={formData.workMode}
                          onChange={handleChange}
                          disabled={!canPost}
                        >
                          <MenuItem value="">
                            <em>Select work mode</em>
                          </MenuItem>
                          <MenuItem value="office">Work From Office</MenuItem>
                          <MenuItem value="hybrid">Hybrid</MenuItem>
                          <MenuItem value="remote">Remote</MenuItem>
                        </TextField>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={panelSx("#f8fbff", "#dbe7ff")}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.2,
                            mb: 1.5,
                          }}
                        >
                          <Box sx={iconBoxSx("#e7f0ff", INDIGO)}>
                            <PaymentsOutlinedIcon fontSize="small" />
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                color: INDIGO,
                              }}
                            >
                              Salary Range *
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              Enter salary range
                            </Typography>
                          </Box>
                        </Box>

                        <TextField
                          fullWidth
                          placeholder="Enter salary range"
                          name="salary"
                          value={formData.salary}
                          onChange={handleChange}
                          disabled={!canPost}
                          error={!!fieldErrors.salary}
                          helperText={fieldErrors.salary}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={panelSx("#fbf7ff", "#eadcff")}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.5 }}>
                          <Box sx={iconBoxSx("#f0e8ff", INDIGO)}>
                            <CardGiftcardOutlinedIcon fontSize="small" />
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 800, color: INDIGO }}>
                              Stipend
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Optional
                            </Typography>
                          </Box>
                        </Box>

                        <TextField
                          fullWidth
                          select
                          name="stipend"
                          value={formData.stipend}
                          onChange={handleChange}
                          disabled={!canPost}
                        >
                          <MenuItem value="">
                            <em>Select stipend</em>
                          </MenuItem>
                          <MenuItem value="Unpaid">Unpaid</MenuItem>
                          <MenuItem value="0–10k">0–10k</MenuItem>
                          <MenuItem value="10k–20k">10k–20k</MenuItem>
                          <MenuItem value="20k–30k">20k–30k</MenuItem>
                          <MenuItem value="30k–40k">30k–40k</MenuItem>
                          <MenuItem value="40k–50k">40k–50k</MenuItem>
                          <MenuItem value="50k and above">50k and above</MenuItem>
                        </TextField>
                      </Box>
                    </Grid>



                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 2.5,
                          borderRadius: 3,
                          border: "1px solid #dbeef0",
                          background: "linear-gradient(180deg, #f7fffe 0%, #ffffff 100%)",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                          <Box sx={iconBoxSx("#dff7f2", INDIGO)}>
                            <WorkHistoryOutlinedIcon fontSize="small" />
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 800, color: INDIGO }}>
                              Experience Range *
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Select the minimum and maximum years required for this role.
                            </Typography>
                          </Box>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: INDIGO }}>
                              Minimum Experience (Years)
                            </Typography>
                            <TextField
                              fullWidth
                              select
                              name="minExperience"
                              value={formData.minExperience}
                              onChange={handleChange}
                              disabled={!canPost}
                              error={!!fieldErrors.experienceRange}
                              helperText={fieldErrors.experienceRange}
                            >
                              <MenuItem value="">
                                <em>Select minimum experience</em>
                              </MenuItem>

                              {[...Array(21)].map((_, i) => (
                                <MenuItem key={i} value={i}>
                                  {i} {i === 1 ? "Year" : "Years"}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: INDIGO }}>
                              Maximum Experience (Years)
                            </Typography>
                            <TextField
                              fullWidth
                              select
                              name="maxExperience"
                              value={formData.maxExperience}
                              onChange={handleChange}
                              disabled={!canPost}
                              error={!!fieldErrors.experienceRange}
                              helperText={fieldErrors.experienceRange}
                            >
                              <MenuItem value="">
                                <em>Select maximum experience</em>
                              </MenuItem>
                              {[...Array(21)].map((_, i) => (
                                <MenuItem key={i} value={i}>
                                  {i} {i === 1 ? "Year" : "Years"}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </Grid>

                        {formData.minExperience !== "" && formData.maxExperience !== "" && (
                          <Typography variant="body2" sx={{ mt: 1.2, color: "#4b5563" }}>
                            Selected: {formData.minExperience} to {formData.maxExperience} Years
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={panelSx("#f7fffe", "#dbeef0")}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                          <Box sx={iconBoxSx("#dff7f2", INDIGO)}>
                            <DescriptionOutlinedIcon fontSize="small" />
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 800, color: INDIGO }}>
                              Job Description *
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Describe the job role, responsibilities, and expectations
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 2,
                            overflow: "hidden",
                            backgroundColor: "#fff",
                            "& .quill": { backgroundColor: "#fff" },
                          }}
                        >
                          <ReactQuill
                            key={quillKey}
                            theme="snow"
                            value={formData.description}
                            onChange={handleQuillChange}
                            disabled={!canPost}
                            modules={{
                              toolbar: [
                                [{ header: [1, 2, 3, false] }],
                                ["bold", "italic"],
                                [{ list: "ordered" }, { list: "bullet" }],
                                ["clean"],
                              ],
                            }}
                            style={{ height: "180px" }}
                            placeholder="Describe the job role, responsibilities, and expectations..."
                          />
                        </Box>

                        <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary", textAlign: "right" }}>
                          {getWordCount(formData.description)} words
                        </Typography>

                        {fieldErrors.description && (
                          <Typography variant="caption" color="error">
                            {fieldErrors.description}
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={panelSx("#f7fffe", "#dbeef0")}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                          <Box sx={iconBoxSx("#dff7f2", INDIGO)}>
                            <AssignmentTurnedInOutlinedIcon fontSize="small" />
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 800, color: INDIGO }}>
                              Requirements (one per line) *
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Enter each requirement on a new line
                            </Typography>
                          </Box>
                        </Box>

                        <TextField
                          fullWidth
                          multiline
                          rows={8}
                          placeholder={"Enter each requirement on a new line"}
                          name="requirements"
                          value={formData.requirements}
                          onChange={handleChange}
                          disabled={!canPost}
                          error={!!fieldErrors.requirements}
                          helperText={fieldErrors.requirements || "Example: 3+ years of experience in React.js"}
                          sx={{
                            "& .MuiInputBase-input": {
                              whiteSpace: "pre-line",
                              wordBreak: "break-word",
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={panelSx("#fffaf7", "#f3e0d2")}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                          <Box sx={iconBoxSx("#fff1e8", INDIGO)}>
                            <WorkHistoryOutlinedIcon fontSize="small" />
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 800, color: INDIGO }}>
                              Screening Questions (Optional)
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Add 1 to 6 short questions for candidates
                            </Typography>
                          </Box>
                        </Box>

                        {screeningQuestions.map((question, index) => (
                          <Box key={index} sx={{ mb: 2 }}>
                            <TextField
                              fullWidth
                              placeholder={`Question ${index + 1}`}
                              value={question}
                              onChange={(e) => handleScreeningQuestionChange(index, e.target.value)}
                              disabled={!canPost}
                            />

                            {screeningQuestions.length > 1 && (
                              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                                <Button
                                  size="small"
                                  onClick={() => removeScreeningQuestion(index)}
                                  disabled={!canPost}
                                  sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    color: "#dc2626",
                                  }}
                                >
                                  Remove
                                </Button>
                              </Box>
                            )}
                          </Box>
                        ))}

                        {screeningQuestions.length < 6 && (
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={addScreeningQuestion}
                            disabled={!canPost}
                            sx={{
                              borderRadius: 2,
                              py: 1.2,
                              textTransform: "none",
                              fontWeight: 800,
                              borderColor: "#f59e0b",
                              color: "#c2410c",
                              "&:hover": {
                                borderColor: "#ea580c",
                                backgroundColor: "#fff7ed",
                              },
                            }}
                          >
                            + Add Question
                          </Button>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", gap: 2, pt: 1 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          startIcon={<VisibilityOutlinedIcon />}
                          disabled={loading || !canPost}
                          sx={{
                            px: 3,
                            py: 1.3,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 800,

                            backgroundColor: "#4F46E5",
                            color: "#FFFFFF",

                            border: "1px solid #4F46E5",

                            boxShadow: "0 10px 24px rgba(79,70,229,0.25)",

                            "&:hover": {
                              backgroundColor: "#4338CA",
                              borderColor: "#4338CA",
                              boxShadow: "0 10px 24px rgba(79,70,229,0.35)",
                            },
                          }}
                        >
                          Preview Job
                        </Button>

                        <Button
                          variant="contained"
                          size="large"
                          onClick={() => navigate("/admin/dashboard")}
                          sx={{
                            px: 3,
                            py: 1.3,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 700,
                            borderColor: "#a5b4fc",
                            color: "#FFFFFF",
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              );
            })()}
          </form>
        </Paper>
        <Dialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "22px",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0,0,0,0.16)",
              background: "#ffffff",
              maxHeight: "90vh",
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(15, 23, 42, 0.38)",
              backdropFilter: "blur(5px)",
            },
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2.2,
              background: "linear-gradient(180deg, #f7fffd 0%, #eefaf7 100%)",
              borderBottom: "1px solid #e6ecef",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#dff7f2",
                    color: INDIGO,
                  }}
                >
                  <VisibilityOutlinedIcon sx={{ fontSize: 30, color: INDIGO }} />
                </Box>

                <Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: INDIGO, lineHeight: 1.1 }}
                  >
                    Preview Job Post
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.8, color: "#6b7280", maxWidth: 620 }}
                  >
                    Please review the job details before publishing. You can go back to
                    edit if any changes are needed.
                  </Typography>
                </Box>
              </Box>

              <IconButton
                onClick={() => setPreviewOpen(false)}
                sx={{
                  width: 38,
                  height: 38,
                  backgroundColor: "#f3f4f6",
                  "&:hover": { backgroundColor: "#e5e7eb" },
                }}
              >
                <CloseIcon sx={{ color: INDIGO, fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>

          <DialogContent sx={{ p: 2.5, backgroundColor: "#fff" }}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: "18px",
                border: "1px solid #dbe7ef",
                background: "linear-gradient(180deg, #fbfdff 0%, #ffffff 100%)",
                boxShadow: "0 8px 20px rgba(15, 23, 42, 0.04)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 900,
                      color: INDIGO,
                      letterSpacing: "-0.03em",
                      lineHeight: 1.05,
                    }}
                  >
                    {formData.title || "Job Title"}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.1,
                      mt: 1.2,
                      color: "#4b5563",
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                      <BusinessOutlinedIcon sx={{ fontSize: 17, color: "#6b7280" }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formData.companyName || "Company Name"}
                      </Typography>
                    </Box>

                    <Typography sx={{ color: "#9ca3af" }}>•</Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                      <LocationOnOutlinedIcon sx={{ fontSize: 17, color: "#6b7280" }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formData.location || "Location"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    px: 1.8,
                    py: 0.9,
                    borderRadius: 999,
                    border: "1px solid #bde7d6",
                    backgroundColor: "#eefbf5",
                    color: "#0f7a53",
                    fontWeight: 800,
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    whiteSpace: "nowrap",
                  }}
                >
                  <span style={{ fontSize: 14 }}>✓</span>
                  Ready to publish
                </Box>
              </Box>


              <Box
                sx={{
                  mt: 2.4,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.6,
                }}
              >
                <Box
                  sx={{
                    p: 1.8,
                    borderRadius: "16px",
                    background: "#f2fbf8",
                    border: "1px solid #d9f0ea",
                    minHeight: 88,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.3 }}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "14px",
                        backgroundColor: "#dff7f2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: INDIGO,
                      }}
                    >
                      <WorkOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#6b7280", display: "block" }}>
                        Work Mode
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f766e", textTransform: "capitalize" }}>
                        {formData.workMode || "Not selected"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: 1.8,
                    borderRadius: "16px",
                    background: "#f8fbff",
                    border: "1px solid #dbe7ff",
                    minHeight: 88,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.3 }}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "14px",
                        backgroundColor: "#e7f0ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: INDIGO,
                      }}
                    >
                      <PaymentsOutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#6b7280", display: "block" }}>
                        Salary
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1d4ed8" }}>
                        {formData.salary || "Not provided"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: 1.8,
                    borderRadius: "16px",
                    background: "#fbf7ff",
                    border: "1px solid #eadcff",
                    minHeight: 88,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.3 }}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "14px",
                        backgroundColor: "#f0e8ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: INDIGO,
                      }}
                    >
                      <CardGiftcardOutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#6b7280", display: "block" }}>
                        Stipend
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#7c3aed" }}>
                        {formData.stipend || "Not selected"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: 1.8,
                    borderRadius: "16px",
                    background: "#f7fbff",
                    border: "1px solid #d7e9ff",
                    minHeight: 88,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.3 }}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "14px",
                        backgroundColor: "#e8f1ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: INDIGO,
                      }}
                    >
                      <WorkHistoryOutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#6b7280", display: "block" }}>
                        Experience
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#2563eb" }}>
                        {formData.minExperience !== "" && formData.maxExperience !== ""
                          ? `${formData.minExperience} - ${formData.maxExperience} Years`
                          : "Not selected"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                mt: 2.5,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: "18px",
                  border: "1px solid #e5e7eb",
                  backgroundColor: "#fff",
                  boxShadow: "0 8px 18px rgba(15, 23, 42, 0.03)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.8 }}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "11px",
                      backgroundColor: "#dff7f2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: INDIGO,
                    }}
                  >
                    <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: INDIGO }}>
                    Job Description
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: "16px",
                    backgroundColor: "#fafafa",
                    border: "1px solid #eef0f3",
                    minHeight: 220,
                    color: "#374151",
                    lineHeight: 1.7,
                    "& p": { mb: 1.2 },
                    "& ul": { pl: 3, mt: 1 },
                    "& li": { mb: 0.5 },
                  }}
                  dangerouslySetInnerHTML={{ __html: formData.description || "<p>No description added</p>" }}
                />
              </Box>

              <Box
                sx={{
                  p: 2.5,
                  borderRadius: "18px",
                  border: "1px solid #e5e7eb",
                  backgroundColor: "#fff",
                  boxShadow: "0 8px 18px rgba(15, 23, 42, 0.03)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.8 }}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "11px",
                      backgroundColor: "#e6faf0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#0f7a53",
                    }}
                  >
                    <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: INDIGO }}>
                    Requirements
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: "16px",
                    backgroundColor: "#fafafa",
                    border: "1px solid #eef0f3",
                    minHeight: 220,
                  }}
                >
                  {formData.requirements ? (
                    formData.requirements
                      .split("\n")
                      .filter((r) => r.trim())
                      .map((req, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.2,
                            mb: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              mt: 0.75,
                              width: 9,
                              height: 9,
                              borderRadius: "50%",
                              backgroundColor: INDIGO,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.7 }}>
                            {req}
                          </Typography>
                        </Box>
                      ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No requirements added
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
            {screeningQuestions.filter((q) => q.trim()).length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: INDIGO,
                    mb: 2,
                  }}
                >
                  Screening Questions
                </Typography>

                <Box
                  sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 3,
                    p: 3,
                    backgroundColor: "#fffaf7",
                  }}
                >
                  {screeningQuestions
                    .filter((q) => q.trim())
                    .map((question, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          mb: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: "#ea580c",
                            minWidth: 24,
                          }}
                        >
                          {index + 1}.
                        </Typography>

                        <Typography
                          sx={{
                            color: "#374151",
                            lineHeight: 1.7,
                          }}
                        >
                          {question}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </Box>
            )}

            <Box
              sx={{
                mt: 2.5,
                p: 2,
                borderRadius: "16px",
                backgroundColor: "#fff8e7",
                border: "1px solid #f5d48b",
                color: "#8a5b00",
                display: "flex",
                alignItems: "center",
                gap: 1.2,
              }}
            >
              <LightbulbOutlinedIcon sx={{ color: INDIGO, fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Please review all details carefully before publishing.
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2.2,
              borderTop: "1px solid #e5e7eb",
              gap: 1.3,
              backgroundColor: "#fff",
            }}
          >
            <Button
              onClick={() => setPreviewOpen(false)}
              variant="outlined"
              sx={{
                borderRadius: 2,
                px: 2.8,
                py: 1.05,
                textTransform: "none",
                fontWeight: 700,
                borderColor: "#0f766e",
                color: "#0f766e",
                "&:hover": {
                  borderColor: "#0b5f58",
                  backgroundColor: "rgba(15,118,110,0.04)",
                },
              }}
            >
              Back to Edit
            </Button>

            <Button
              variant="contained"
              onClick={handleConfirmPost}
              disabled={loading}
              sx={{
                borderRadius: 2,
                px: 2.8,
                py: 1.05,
                textTransform: "none",
                fontWeight: 800,
                boxShadow: "none",
                backgroundColor: "#0f766e",
                "&:hover": { backgroundColor: "#0b5f58", boxShadow: "none" },
              }}
            >
              {loading ? "Posting..." : "Post Job"}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Starter Plan Limit Dialog */}
        <Dialog
          open={showLimitPopup && (limitPlanType === 'starter' || (limitPlanType !== 'professional' && limitPlanType !== 'professional_overage' && user?.plan?.toLowerCase() === 'monthly'))}
          onClose={() => setShowLimitPopup(false)}
        >
          <DialogTitle>Starter Plan Limit Reached</DialogTitle>
          <DialogContent>
            <Typography>
              You have used all 10 Starter Plan job postings.
            </Typography>
            <Typography sx={{ mt: 1 }}>
              To continue posting jobs, purchase Starter Overage Add-ons.
              Extra jobs include additional application processing credits.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowLimitPopup(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleBuyExtra}>Buy Overage Add-ons</Button>
          </DialogActions>
        </Dialog>

        {/* Professional Plan Credits Exhausted Dialog */}
        <Dialog
          open={showLimitPopup && limitPlanType === 'professional'}
          onClose={() => setShowLimitPopup(false)}
        >
          <DialogTitle>Professional Plan Credits Exhausted</DialogTitle>
          <DialogContent>
            <Typography>
              Your Professional Plan job posting credits are fully used.
            </Typography>
            <Typography sx={{ mt: 1 }}>
              Purchase Professional Overage Add-ons to continue posting jobs.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowLimitPopup(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleBuyExtra}>Buy Professional Overage Add-ons</Button>
          </DialogActions>
        </Dialog>

        {/* Professional Overage Credits Exhausted Dialog */}
        <Dialog
          open={showLimitPopup && limitPlanType === 'professional_overage'}
          onClose={() => setShowLimitPopup(false)}
        >
          <DialogTitle>Professional Overage Credits Exhausted</DialogTitle>
          <DialogContent>
            <Typography>
              No Professional Overage job credits remaining.
            </Typography>
            <Typography sx={{ mt: 1 }}>
              Purchase additional Professional Overage Add-ons to continue.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowLimitPopup(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleBuyExtra}>Buy Professional Overage Add-ons</Button>
          </DialogActions>
        </Dialog>
      </Container>
  );
}

export default PostJob;
